/**
 * Guest-aware cart client.
 *
 * When the shopper is signed in we hit the authed `/marketplace/cart*`
 * endpoints. When they're a guest we use a client-generated `guest_id`
 * (persisted in localStorage) against the public `/marketplace/guest-cart*`
 * endpoints. On sign-in, `mergeGuestCartIfAny()` folds the guest cart into the
 * user's cart and clears the guest id.
 */
import { api, getToken } from "@/lib/api";

const GUEST_KEY = "socibaba_guest_id";

export function getGuestId(create = true): string | null {
  let id = localStorage.getItem(GUEST_KEY);
  if (!id && create) {
    const raw = (globalThis.crypto?.randomUUID?.() ||
      Math.random().toString(36).slice(2) + Date.now().toString(36));
    id = ("g_" + raw).replace(/[^a-zA-Z0-9-_]/g, "").slice(0, 60);
    localStorage.setItem(GUEST_KEY, id);
  }
  return id;
}

export function clearGuestId() {
  localStorage.removeItem(GUEST_KEY);
}

/** Adopt a guest id from a resume link (sanitised). Returns the applied id or "". */
export function setGuestId(id: string): string {
  const clean = (id || "").replace(/[^a-zA-Z0-9-_]/g, "").slice(0, 60);
  if (clean.length >= 8) localStorage.setItem(GUEST_KEY, clean);
  return clean.length >= 8 ? clean : "";
}

export function isAuthed(): boolean {
  return !!getToken();
}

export async function fetchCart() {
  if (isAuthed()) return (await api.get("/marketplace/cart")).data;
  const gid = getGuestId();
  return (await api.get(`/marketplace/guest-cart?guest_id=${gid}`)).data;
}

export async function addToCart(p: {
  product_id: string; quantity?: number; variant_id?: string; bundle_group?: string;
}) {
  const body: any = { quantity: 1, ...p };
  if (isAuthed()) return (await api.post("/marketplace/cart/items", body)).data;
  return (await api.post("/marketplace/guest-cart/items", { ...body, guest_id: getGuestId() })).data;
}

export async function updateCartItem(p: { product_id: string; variant_id?: string; quantity: number }) {
  if (isAuthed()) return (await api.patch("/marketplace/cart/items", p)).data;
  return (await api.patch("/marketplace/guest-cart/items", { ...p, guest_id: getGuestId() })).data;
}

export async function removeCartItem(p: { product_id: string; variant_id?: string }) {
  if (isAuthed()) {
    return (await api.delete(`/marketplace/cart/items/${p.product_id}`,
      { params: p.variant_id ? { variant_id: p.variant_id } : {} })).data;
  }
  return (await api.delete(`/marketplace/guest-cart/items/${p.product_id}`,
    { params: { guest_id: getGuestId(), ...(p.variant_id ? { variant_id: p.variant_id } : {}) } })).data;
}

/** Fold a guest cart into the signed-in user's cart, then clear the guest id. */
export async function mergeGuestCartIfAny() {
  const gid = localStorage.getItem(GUEST_KEY);
  if (!gid || !isAuthed()) return;
  try {
    await api.post("/marketplace/cart/merge", { guest_id: gid });
  } finally {
    clearGuestId();
  }
}
